import { Fragment, useState } from "react";
import {
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { DataTable } from "~/components/data-table";
import { Link as RouterLink } from "react-router-dom";
import { ImpactCell } from "~/components/Cells";
import { AssetOrderField, OrderDirection } from "~/operations";
import { UseSortReturn } from "~/pages/inventory/hooks/useSort";
import { Paginate, PaginateProps } from "~/components/pagination/paginate";
import {
  ASSET_TABLE_INITIAL_PAGE_RANGE,
  Pagination,
  PaginationRange,
  UsePaginationProps,
} from "~/components/pagination";
import { FormatRelativeDateWithTime } from "~/lib/date";
import { AssetFieldsFragment } from "~/operations/types";
import { useAssetContextualLink } from "~/pages/space/vulnerabilities/components/exposed-assets/hooks/useAssetContextualLink";
import { AssetContextualLinkType } from "~/pages/space/vulnerabilities/components/exposed-assets";

type AssetNode = NonNullable<
  NonNullable<AssetFieldsFragment["edges"]>[number]["node"]
>;

interface InitiativeAssetsTableProps {
  assets: Array<AssetNode | null>;
  paginationProps?: PaginateProps;
  sort: UseSortReturn<{ field: AssetOrderField; direction: OrderDirection }>;
  filteredTotalCount: number;
  fetchMore?: UsePaginationProps["fetchMore"];
  pageInfo?: UsePaginationProps["pageInfo"];
  scopeParams: URLSearchParams;
}

export function InitiativeAssetsTable({
  assets = [],
  sort,
  filteredTotalCount,
  fetchMore,
  pageInfo,
  paginationProps,
  scopeParams,
}: InitiativeAssetsTableProps) {
  const [pageItems, setPageItems] = useState<PaginationRange>(
    ASSET_TABLE_INITIAL_PAGE_RANGE,
  );

  const sortDirection =
    sort.orderBy.direction === OrderDirection.Desc ? "desc" : "asc";

  return (
    <Fragment>
      <DataTable id="initiative-assets-table">
        <TableHead>
          <TableRow>
            <TableCell width="10%">
              <TableSortLabel
                onClick={() => sort.handleSortClick(AssetOrderField.RiskValue)}
                direction={sortDirection}
                active={sort.orderBy.field === AssetOrderField.RiskValue}
              >
                Risk
              </TableSortLabel>
            </TableCell>
            <TableCell width="45%">
              <TableSortLabel
                onClick={() => sort.handleSortClick(AssetOrderField.Name)}
                direction={sortDirection}
                active={sort.orderBy.field === AssetOrderField.Name}
              >
                Asset name
              </TableSortLabel>
            </TableCell>
            <TableCell width="20%">
              <TableSortLabel
                onClick={() => sort.handleSortClick(AssetOrderField.Platform)}
                direction={sortDirection}
                active={sort.orderBy.field === AssetOrderField.Platform}
              >
                Platform
              </TableSortLabel>
            </TableCell>
            <TableCell width="25%" sx={{ pr: 4 }}>
              <TableSortLabel
                onClick={() =>
                  sort.handleSortClick(AssetOrderField.LastUpdated)
                }
                direction={sortDirection}
                active={sort.orderBy.field === AssetOrderField.LastUpdated}
              >
                Last updated
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {assets.map((asset) => {
            if (!asset) return null;
            const formattedDate = asset.updatedAt
              ? FormatRelativeDateWithTime(asset.updatedAt)
              : "";
            const assetId = asset.mrn.split("/").pop() || "";
            const { contextUrl: assetHref } = useAssetContextualLink({
              assetId: assetId,
              type: AssetContextualLinkType.Asset,
              scopeParams: scopeParams,
            });
            return (
              <TableRow
                key={asset.id}
                component={RouterLink}
                to={assetHref}
                sx={{ cursor: "pointer" }}
              >
                {asset.score?.riskRating && (
                  <ImpactCell
                    impact={asset.score.riskValue || 0}
                    rating={asset.score.riskRating}
                    isActive
                  />
                )}
                {!asset.score?.riskRating && <TableCell>-</TableCell>}
                <TableCell sx={{ overflowWrap: "anywhere" }}>
                  {asset.name}
                </TableCell>
                <TableCell>{asset.platform?.name}</TableCell>
                <TableCell sx={{ pr: 4 }}>{formattedDate}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </DataTable>
      {paginationProps && <Paginate {...paginationProps} />}
      {!paginationProps && fetchMore && pageInfo && (
        <Pagination
          fetchMore={fetchMore}
          totalCount={filteredTotalCount || 0}
          setPageItems={setPageItems}
          pageInfo={pageInfo}
          defaultPageSize={ASSET_TABLE_INITIAL_PAGE_RANGE.to}
        />
      )}
    </Fragment>
  );
}
